import React from 'react';

import { flip, offset, shift } from '@floating-ui/react';
import { DraftDecorator, EditorState, Modifier, SelectionState } from 'draft-js';
import EmojiPickerReact, { EmojiStyle, EmojiClickData } from 'emoji-picker-react';

import Icon from 'components/shared/Icon';
import Smile from 'components/shared/Icon/icons/Smile';
import { inlineStylesMap } from 'components/shared/Wysiwyg/customStyles';
import { useWysiwygContext } from 'components/shared/Wysiwyg/Wysiwyg.contex';

import { BaseItemButtonWithPortal, PortalControl } from '../Base';
import { HIDDEN_EMOJIS } from './Emoji.conts';
import s from './Emoji.module.css';
import * as t from './Emoji.types';

export const Emoji = () => {
  const { editor } = useWysiwygContext();
  const portalRef = React.useRef<PortalControl>(null);
  const selectionState = React.useRef<SelectionState | null>(null);

  const openPortal = () => {
    selectionState.current = editor.editorState.getSelection();
    editor.setEditorState(inlineStylesMap.selected.toggle(editor.editorState, 'yes'));
    portalRef.current?.open();
  };

  const removeSelectStyle = () => {
    if (selectionState.current) {
      editor.setEditorState(
        EditorState.forceSelection(inlineStylesMap.selected.toggle(editor.editorState, 'yes'), selectionState.current),
      );
    }
  };

  const insertEmoji = (editorState: EditorState, emoji: string) => {
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity('EMOJI', 'IMMUTABLE', {
      emojiUnicode: emoji,
    });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const currentSelectionState = editorState.getSelection();

    const afterRemovalContentState = Modifier.removeRange(contentState, currentSelectionState, 'backward');
    const targetSelection = afterRemovalContentState.getSelectionAfter();

    const emojiAddedContent = Modifier.insertText(
      afterRemovalContentState,
      targetSelection,
      emoji,
      undefined,
      entityKey,
    );

    return EditorState.forceSelection(
      EditorState.push(editorState, emojiAddedContent, 'insert-fragment'),
      emojiAddedContent.getSelectionAfter(),
    );
  };

  const onEmojiClick = (emoji: EmojiClickData) => {
    const newEditorState = insertEmoji(editor.editorState, emoji.emoji);
    editor.handleChange(newEditorState);
  };

  return (
    <BaseItemButtonWithPortal
      ref={portalRef}
      portalRef={editor.refs.portalRef}
      portalContent={<Portal onEmojiClick={onEmojiClick} />}
      onMouseDown={openPortal}
      onBlur={removeSelectStyle}
      floatingOptions={{ middleware: [offset(), flip(), shift()], placement: 'bottom' }}
    >
      <Icon width="20" height="20" component={Smile} className={s.icon} />
    </BaseItemButtonWithPortal>
  );
};

type PortalProps = {
  onEmojiClick: (emoji: EmojiClickData) => void;
};

const Portal = (props: PortalProps) => {
  return (
    <div>
      <EmojiPickerReact
        emojiStyle={EmojiStyle.NATIVE}
        lazyLoadEmojis={true}
        allowExpandReactions={false}
        onEmojiClick={props.onEmojiClick}
        hiddenEmojis={HIDDEN_EMOJIS}
        width={340}
        height={300}
        previewConfig={{
          showPreview: false,
        }}
        reactionsDefaultOpen={false}
      />
    </div>
  );
};

export const findEmojiEntities: DraftDecorator['strategy'] = (contentBlock, callback, contentState) => {
  contentBlock.findEntityRanges((character) => {
    const entityKey = character.getEntity();
    return entityKey !== null && contentState.getEntity(entityKey).getType() === 'EMOJI';
  }, callback);
};

export const EmojiDecorator = (isReadOnly = false, props: t.EmojiDecoratorProps) => {
  const { children, entityKey, contentState } = props;

  if (isReadOnly) {
    const entity = contentState.getEntity(entityKey);
    const code = entity.getData().emojiUnicode.codePointAt(0).toString(16);
    const src = `https://cdn.jsdelivr.net/npm/emoji-datasource-apple/img/apple/64/${code}.png`;
    return (
      <img
        src={src}
        style={{
          display: 'inline',
          maxWidth: 'none',
          minWidth: 'auto',
          width: '16px',
        }}
      />
    );
  }

  return children;
};
